import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Plus, Pencil, Trash2, Undo2, ChevronRight, CheckCircle2, Clock,
    Package, Tag, Settings, Ticket, Receipt, BookOpen, Calendar, Crown,
} from "lucide-react";

const RESOURCE_OPTIONS = [
    { value: "all", label: "All resources", Icon: null },
    { value: "product", label: "Products", Icon: Package },
    { value: "category", label: "Categories", Icon: Tag },
    { value: "attribute", label: "Attributes", Icon: Settings },
    { value: "coupon", label: "Coupons", Icon: Ticket },
    { value: "order", label: "Orders", Icon: Receipt },
    { value: "course", label: "Courses", Icon: BookOpen },
    { value: "schedule", label: "Schedules", Icon: Calendar },
    { value: "membership", label: "Memberships", Icon: Crown },
];

const ACTION_BADGE = {
    create: { Icon: Plus, label: "Created", className: "bg-emerald-50 text-emerald-700 border border-emerald-100" },
    update: { Icon: Pencil, label: "Updated", className: "bg-amber-50 text-amber-700 border border-amber-100" },
    delete: { Icon: Trash2, label: "Deleted", className: "bg-accent-50 text-accent-700 border border-accent-100" },
};

const resourceIcon = (rt) => RESOURCE_OPTIONS.find((o) => o.value === rt)?.Icon || ChevronRight;
const resourceLabel = (rt) => RESOURCE_OPTIONS.find((o) => o.value === rt)?.label?.replace(/s$/, "") || rt;

/**
 * Returns the list of changed top-level keys between before/after with their
 * old/new values. Skips obviously noisy fields like updated_at.
 */
function buildDiff(before, after) {
    if (!before || !after) return [];
    const skip = new Set(["updated_at", "created_at", "_id"]);
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    const out = [];
    for (const k of keys) {
        if (skip.has(k)) continue;
        const b = before[k];
        const a = after[k];
        if (JSON.stringify(b) === JSON.stringify(a)) continue;
        out.push({ key: k, before: b, after: a });
    }
    return out;
}

function formatValue(v) {
    if (v === null || v === undefined || v === "") return <em className="text-ink-400">empty</em>;
    if (typeof v === "object") return <code className="text-[11px]">{JSON.stringify(v)}</code>;
    return String(v);
}

function ActivityDetailDialog({ activity, onClose, onReverted }) {
    const [busy, setBusy] = useState(false);
    if (!activity) return null;

    const ActionIcon = ACTION_BADGE[activity.action]?.Icon || ChevronRight;
    const diff = buildDiff(activity.before, activity.after);
    const snapshot = activity.action === "delete" ? activity.before : activity.after;

    const revert = async () => {
        if (!confirm(`Revert this ${activity.action} on "${activity.resource_label}"?`)) return;
        setBusy(true);
        try {
            await api.post(`/admin/manager-activities/${activity.id}/revert`);
            toast.success("Reverted");
            onReverted?.();
            onClose();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!activity} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="activity-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
                        <ActionIcon className="h-5 w-5" />
                        {ACTION_BADGE[activity.action]?.label} {resourceLabel(activity.resource_type)}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    <div className="rounded-2xl bg-paper-alt p-4 space-y-1">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-[11px] uppercase tracking-wide text-ink-700">Resource</p>
                                <p className="font-medium text-ink-900 break-all">{activity.resource_label || activity.resource_id}</p>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${ACTION_BADGE[activity.action]?.className || ""}`}>
                                <ActionIcon className="h-3 w-3" />
                                {ACTION_BADGE[activity.action]?.label}
                            </span>
                        </div>
                        <div className="text-[11px] text-ink-700">
                            By <strong className="text-ink-900">{activity.actor_email}</strong>
                            {" · "}
                            <time>{new Date(activity.created_at).toLocaleString()}</time>
                        </div>
                    </div>

                    {activity.action === "update" && diff.length > 0 && (
                        <div className="rounded-2xl border border-gray-100 overflow-hidden">
                            <div className="px-4 py-2 bg-paper-alt border-b border-gray-100 font-medium">
                                Changes ({diff.length})
                            </div>
                            <ul className="divide-y divide-gray-100">
                                {diff.map(({ key, before, after }) => (
                                    <li key={key} className="px-4 py-3 grid grid-cols-3 gap-3 text-xs">
                                        <div className="font-medium text-ink-900 truncate">{key}</div>
                                        <div className="text-ink-700">
                                            <span className="text-[10px] uppercase block text-ink-400">Before</span>
                                            <span className="line-through">{formatValue(before)}</span>
                                        </div>
                                        <div className="text-ink-900">
                                            <span className="text-[10px] uppercase block text-ink-400">After</span>
                                            {formatValue(after)}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {(activity.action === "create" || activity.action === "delete") && snapshot && (
                        <div className="rounded-2xl border border-gray-100 overflow-hidden">
                            <div className="px-4 py-2 bg-paper-alt border-b border-gray-100 font-medium">
                                Snapshot
                            </div>
                            <pre className="text-[11px] bg-white p-3 max-h-72 overflow-auto whitespace-pre-wrap break-all">
                                {JSON.stringify(snapshot, null, 2)}
                            </pre>
                        </div>
                    )}

                    {activity.reverted && (
                        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-900 inline-flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Reverted by {activity.reverted_by} on {new Date(activity.reverted_at).toLocaleString()}
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-full">
                        Close
                    </Button>
                    {!activity.reverted && (
                        <Button
                            type="button"
                            onClick={revert}
                            disabled={busy}
                            className="rounded-full bg-accent-500 hover:bg-accent-600 text-white shadow-cta"
                            data-testid="activity-revert-btn"
                        >
                            <Undo2 className="h-4 w-4 mr-1.5" />
                            {busy ? "Reverting…" : "Revert this change"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function ManagerActivityTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resource, setResource] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all"); // all | pending | reverted
    const [selected, setSelected] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "200" });
            if (resource !== "all") params.set("resource_type", resource);
            const { data } = await api.get(`/admin/manager-activities?${params}`);
            setItems(data || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, [resource]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        if (statusFilter === "pending") return items.filter((a) => !a.reverted);
        if (statusFilter === "reverted") return items.filter((a) => a.reverted);
        return items;
    }, [items, statusFilter]);

    return (
        <div className="space-y-4" data-testid="manager-activity-tab">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-2xl">Shop Manager Activity</h3>
                    <p className="text-sm text-ink-700">
                        Every action taken by a shop manager on Products / Categories / Attributes / Coupons / Orders /
                        Courses / Schedules / Memberships is recorded here. Open any row to inspect the diff and revert if needed.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Select value={resource} onValueChange={setResource}>
                        <SelectTrigger className="w-48 rounded-full" data-testid="activity-filter-resource">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {RESOURCE_OPTIONS.map(({ value, label, Icon }) => (
                                <SelectItem key={value} value={value}>
                                    <span className="inline-flex items-center gap-2">
                                        {Icon && <Icon className="h-3.5 w-3.5" />} {label}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-40 rounded-full" data-testid="activity-filter-status">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All activity</SelectItem>
                            <SelectItem value="pending">Not reverted</SelectItem>
                            <SelectItem value="reverted">Reverted</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="space-y-2">
                {loading && [0, 1, 2].map((i) => (
                    <div key={i} className="h-16 rounded-2xl bg-gray-100 animate-pulse" />
                ))}
                {!loading && filtered.length === 0 && (
                    <div className="text-center py-12 text-ink-700">
                        No activity to show here yet.
                    </div>
                )}
                {!loading && filtered.map((a) => {
                    const ResourceIcon = resourceIcon(a.resource_type);
                    const Badge = ACTION_BADGE[a.action] || {};
                    const ActionIcon = Badge.Icon || ChevronRight;
                    return (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => setSelected(a)}
                            className={`w-full text-left rounded-2xl bg-white border border-gray-100 hover:border-brand-200 hover:shadow-soft transition p-4 flex items-center gap-4 ${a.reverted ? "opacity-60" : ""}`}
                            data-testid={`activity-row-${a.id}`}
                        >
                            <div className="h-10 w-10 rounded-xl bg-paper-alt flex items-center justify-center shrink-0 text-ink-700">
                                <ResourceIcon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${Badge.className || ""}`}>
                                        <ActionIcon className="h-3 w-3" />
                                        {Badge.label}
                                    </span>
                                    <span className="text-[11px] uppercase tracking-wide text-ink-700">
                                        {resourceLabel(a.resource_type)}
                                    </span>
                                    {a.reverted && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase">
                                            <CheckCircle2 className="h-3 w-3" /> Reverted
                                        </span>
                                    )}
                                </div>
                                <div className="font-medium text-ink-900 truncate mt-0.5">
                                    {a.resource_label || a.resource_id}
                                </div>
                                <div className="text-[11px] text-ink-700 inline-flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    <time>{new Date(a.created_at).toLocaleString()}</time>
                                    <span className="mx-1">·</span>
                                    by {a.actor_email}
                                </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-ink-400 shrink-0" />
                        </button>
                    );
                })}
            </div>

            <ActivityDetailDialog
                activity={selected}
                onClose={() => setSelected(null)}
                onReverted={reload}
            />
        </div>
    );
}
